import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from "./login_page/login";
import ProtectedRoute from "./components/ProtectedRoute";
import { GatewayDataProvider } from "./dataContext/GatewayDataContext";
import { AuthProvider } from "./dataContext/AuthContext";
import { ConfirmProvider } from "./components/ui/ConfirmProvider";
import { LoadingState } from "./components/ui/States";
import AppLayout from "./layout/AppLayout";
import Page from "./layout/Page";

// Route-level code splitting: each page is downloaded the first time it is visited.
const UnauthorizedPage = lazy(() => import("./unauthorize_page"));
const Dashboard = lazy(() => import("./dashboard_page/dashboard"));
const CustomerDashboard = lazy(() => import("./dashboard_page/customer_dashboard"));
const SettingsContent = lazy(() => import("./settings/settingsContent"));
const WhiteListTable = lazy(() => import("./gateway_whitelist/whitelist_table"));
const Warranty = lazy(() => import("./warranty/warranty"));
const UserManagementContent = lazy(() => import("./device_info/user_content"));
const GatewayAnalyticsContent = lazy(() => import("./analytics/analytics_content"));
const GatewayConnectivityLogsPage = lazy(() => import("./analytics/gateway_connectivity_logs"));
const GatewayStatusContent = lazy(() => import("./analytics/gateway_status_content"));
const UploadImageContent = lazy(() => import("./gateway_image/upload_image_context"));
const CmsMainContent = lazy(() => import("./cms/cms_content"));
const CmsOrder = lazy(() => import("./cms/cms_order"));
const SubscriptionModelPage = lazy(() => import("./cms/manage_subscription_model"));
const CmsPartnersPage = lazy(() => import("./cms/cms_partners_page"));
const ManageProfileContent = lazy(() => import("./profile/manage_profile_content"));
const CustomerExtendedWarranty = lazy(() => import("./extendedWarranty/CustomerExtendedWarranty"));
const GatewaySubscriptionPage = lazy(() => import("./senzoSubscription/gatewaySubscription"));
const SubscriptionUpgrade = lazy(() => import("./senzoSubscription/SubscriptionUpgrade"));
const SubscriptionRenewal = lazy(() => import("./senzoSubscription/SubscriptionRenewal"));
const ActiveSubscriptions = lazy(() => import("./senzoSubscription/ActiveSubscriptions"));
const PurchaseAddOns = lazy(() => import("./senzoSubscription/PurchaseAddOns"));
const MockPaymentPage = lazy(() => import("./payment/MockPaymentPage"));
const PaymentResultPage = lazy(() => import("./payment/PaymentResultPage"));
const PendingPaymentsPage = lazy(() => import("./payment/PendingPaymentPage"));
const CheckoutPage = lazy(() => import("./payment/CheckoutPage"));
const PaymentHistoryPage = lazy(() => import("./payment/PaymentHistoryPage"));
const PendingOrderPage = lazy(() => import("./order/PendingOrderPage"));
const ManageSubscriptionPlanPage = lazy(() => import("./subscription/ManageSubscriptionPlanPage"));
const AdminSubscriptionManagementPage = lazy(() => import("./admin/AdminSubscriptionManagementPage"));
const AddOnCatalogManagement = lazy(() => import("./admin/AddOnCatalogManagement"));
const GrantAddOnsToGateway = lazy(() => import("./admin/GrantAddOnsToGateway"));
const AdminExtendedWarranty = lazy(() => import("./extendedWarranty/AdminExtendedWarranty"));
const BackfillTrialSubscriptions = lazy(() => import("./admin/BackfillTrialSubscriptions"));
const ProductInfoManagement = lazy(() => import("./admin/ProductInfoManagement"));
const FcmBroadcastManagement = lazy(() => import("./admin/FcmBroadcastManagement"));

// Role groups (unchanged from the previous per-route lists)
const ADMIN = ['super_admin', 'clerical_admin'];
const STAFF = ['super_admin', 'clerical_admin', 'cms_admin', 'cms_clerical', 'account_admin', 'account_clerical'];
const CUSTOMER = ['customer_user'];
const SUPER = ['super_admin'];

const guard = (roles, element) => <ProtectedRoute requiredRoles={roles}>{element}</ProtectedRoute>;

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AuthProvider>
      <GatewayDataProvider>
       <ConfirmProvider>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          style={{ zIndex: 999999 }}
        />
        <Router>
          <Suspense fallback={<LoadingState className="min-h-screen" />}>
          <Routes>
            {/* Full-screen pages (no app shell) */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/payment" element={guard(CUSTOMER, <MockPaymentPage />)} />
            <Route path="/payment-result" element={guard(CUSTOMER, <PaymentResultPage />)} />

            {/* Pages inside the persistent sidebar/topbar shell */}
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={guard(STAFF, <Dashboard />)} />
              <Route path="/settings" element={guard(ADMIN, <Page title="Settings"><SettingsContent /></Page>)} />
              <Route path="/whitelist" element={guard(ADMIN, <Page title="Gateway Whitelist"><WhiteListTable /></Page>)} />
              <Route path="/warranty" element={guard(ADMIN, <Warranty />)} />
              <Route path="/device" element={guard(ADMIN, <Page title="User Management"><UserManagementContent /></Page>)} />
              <Route path="/analytics" element={guard(ADMIN, <Page title="Gateway Analytics"><GatewayAnalyticsContent /></Page>)}>
                <Route path="connectivity" element={<GatewayConnectivityLogsPage />} />
              </Route>
              <Route path="/gatewayConnectStatus" element={guard(ADMIN, <Page title="Gateway Activity"><GatewayStatusContent /></Page>)} />
              <Route path="/image_upload" element={guard(ADMIN, <Page title="Gateway Image Upload"><UploadImageContent /></Page>)} />

              <Route path="/cms" element={guard(STAFF, <Page title="CMS Management"><CmsMainContent /></Page>)} />
              <Route path="/cms/orders/:selectedOrganizationId?" element={guard(STAFF, <CmsOrder />)} />
              <Route path="/cms/model" element={guard(['account_admin', 'account_clerical'], <SubscriptionModelPage />)} />
              <Route path="/cms/partners" element={guard(['super_admin', 'clerical_admin', 'account_admin', 'account_clerical'], <Page title="Manage Partners"><CmsPartnersPage /></Page>)} />

              {/* /profile had no role guard before - kept as-is */}
              <Route path="/profile" element={<Page title="Manage Profile"><ManageProfileContent /></Page>} />

              <Route path="/customer_dashboard" element={guard(CUSTOMER, <CustomerDashboard />)} />
              <Route path="/warranty/extended" element={guard(CUSTOMER, <CustomerExtendedWarranty />)} />
              <Route path="/customer_subscription" element={guard(CUSTOMER, <GatewaySubscriptionPage />)} />
              <Route path="/subscription" element={guard(CUSTOMER, <GatewaySubscriptionPage />)} />
              <Route path="/subscription/upgrade" element={guard(CUSTOMER, <SubscriptionUpgrade />)} />
              <Route path="/subscription/renew" element={guard(CUSTOMER, <SubscriptionRenewal />)} />
              <Route path="/subscription/active" element={guard(CUSTOMER, <ActiveSubscriptions />)} />
              <Route path="/subscription/purchase-addons" element={guard(CUSTOMER, <PurchaseAddOns />)} />
              <Route path="/payments/pending" element={guard(CUSTOMER, <PendingPaymentsPage />)} />
              <Route path="/checkout" element={guard(CUSTOMER, <CheckoutPage />)} />
              <Route path="/payments/history" element={guard(CUSTOMER, <PaymentHistoryPage />)} />
              <Route path="/order/pending" element={guard(CUSTOMER, <PendingOrderPage />)} />

              <Route path="/subscription/plans" element={guard(ADMIN, <ManageSubscriptionPlanPage />)} />
              <Route path="/admin/subscriptions" element={guard(SUPER, <AdminSubscriptionManagementPage />)} />
              <Route path="/admin/addons/catalog" element={guard(SUPER, <AddOnCatalogManagement />)} />
              <Route path="/admin/addons/grant" element={guard(SUPER, <GrantAddOnsToGateway />)} />
              <Route path="/admin/warranty/extended" element={guard(SUPER, <AdminExtendedWarranty />)} />
              <Route path="/admin/subscriptions/backfill-trial" element={guard(SUPER, <BackfillTrialSubscriptions />)} />
              <Route path="/admin/product-info" element={guard(SUPER, <ProductInfoManagement />)} />
              <Route path="/admin/notifications/broadcast" element={guard(SUPER, <FcmBroadcastManagement />)} />
            </Route>
          </Routes>
          </Suspense>
        </Router>
       </ConfirmProvider>
    </GatewayDataProvider>
    </AuthProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/reportWebVitals
reportWebVitals();
